<script setup lang="ts">
import type { ContentDetailsAssignment, ReportContentVisit } from '~/models/Report'
import type { GroupMember } from '~/models/Group'
import type { BaseItem } from '~/models/Content/BaseItem'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import useProductStore from '~/stores/product'
import { ColorTheme } from '~/models/Content/Color'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useFormWriter from '~/composables/useFormWriter'
import { useColorTheme } from '~/composables/useColorTheme'
import FormWriterSkeleton from '~/components/skeletons/FormWriterSkeleton.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import FormWriter from '~/components/formwriter/FormWriter.vue'

const props = defineProps<{
  content: BaseItem
  visit?: ReportContentVisit
  groupMembers?: GroupMember[]
  selectedUserId?: string
  relation?: ContentDetailsAssignment
}>()

const { t } = useI18n()
const route = useRoute()
const { style } = useColorTheme(ColorTheme.BlueOrange)
const { buildResourceUrl } = useUrlBuilder()
const { getProductFromResource } = useProductStore()

const isAssignmentPage = computed(() => route.query.section === 'assignment')
const hasRequiredContext = computed(() => !!(props.content && props.relation?.assignmentKey && props.selectedUserId && props.groupMembers?.length))
const product = computed(() => getProductFromResource(props.content))
const relatedGroupMember = computed(() => props.selectedUserId ? props.groupMembers?.find(member => member.userId === props.selectedUserId) : undefined)
const assignmentId = computed(() => props.relation?.assignmentKey ?? '')
const formWriterIdComputed = computed(() => assignmentId.value?.startsWith('form_writer-') ? Number(assignmentId.value.slice('form_writer-'.length)) : 0)
const formWriterEnabled = computed(() => formWriterIdComputed.value > 0 && hasRequiredContext.value)

const {
  data: formWriterData,
  assignment,
  metadata,
  isLoadingFormData,
  isError: formWriterError
} = useFormWriter(
  formWriterIdComputed,
  props.selectedUserId,
  true,
  formWriterEnabled
)

const isFormWriterReady = computed(() => {
  return formWriterIdComputed.value > 0
    && hasRequiredContext.value
    && formWriterData.value
    && !isLoadingFormData.value
})
const isError = computed(() =>
  formWriterEnabled.value
  && (formWriterError.value || (!formWriterData.value && !isLoadingFormData.value))
)
const subjectOrProductName = computed(() => {
  if (!product.value)
    return ''
  return product.value.subjects?.length ? t(`metadata.subjects.${product.value.subjects[0]}`) : ''
})
const studentName = computed(() => relatedGroupMember.value ? `${relatedGroupMember.value.firstName} ${relatedGroupMember.value.lastName}` : '')
const contentUrl = computed(() => buildResourceUrl(props.content))
const formWriterName = computed(() => formWriterData.value?.title ?? '')

const deliveryStatus = computed(() => {
  if (assignment.value?.answer && assignment.value.answer.length > 0) {
    const nonDraftAnswers = assignment.value.answer.filter(answer => !answer.isDraft)
    if (nonDraftAnswers.length > 0) {
      return t('reports.formwriter.delivered')
    }
    return t('reports.formwriter.inProgress')
  }

  if (metadata.value && metadata.value.fields && metadata.value.fields.length > 0) {
    return t('reports.formwriter.inProgress')
  }

  return t('reports.formwriter.notDelivered')
})

// Translation computed properties for v-text
const studentLabelText = computed(() => `${t('reports.formwriter.student')}:`)
const activityLabelText = computed(() => `${t('reports.formwriter.activity')}:`)
const nameLabelText = computed(() => `${t('reports.formwriter.name')}:`)
const statusLabelText = computed(() => `${t('reports.formwriter.status')}:`)
const noAssignmentDataText = computed(() => t('reports.noAssignmentData'))
</script>

<template>
  <div>
    <div class="mb-1 flex flex-wrap items-center gap-2">
      <span
        v-if="content.label"
        class="bg-seagreen-60 text-seagreen-5 rounded-md px-2 py-1 text-xs font-semibold sm:px-3 sm:py-1.5 sm:text-sm"
        v-text="t(`labels.${content.label || content.contentTypeIdentifier}`)"
      />
      <span
        v-if="subjectOrProductName"
        class="bg-seagreen-60 text-seagreen-5 rounded-md px-2 py-1 text-xs font-semibold sm:px-3 sm:py-1.5 sm:text-sm"
        v-text="subjectOrProductName"
      />
    </div>

    <div class="bg-gray-5 mb-6 rounded-lg py-4">
      <div class="space-y-3 text-base">
        <div v-if="studentName" class="flex flex-col sm:flex-row">
          <span class="text-gray-70 font-bold sm:w-32" v-text="studentLabelText" />
          <span class="text-gray-90" v-text="studentName" />
        </div>

        <div v-if="contentUrl && content.title" class="flex flex-col sm:flex-row">
          <span class="text-gray-70 font-bold sm:w-32" v-text="activityLabelText" />
          <RouterLink
            :to="contentUrl"
            class="text-seagreen-60 hover:text-seagreen-70 underline transition-colors hover:underline"
          >
            {{ content.title }}
          </RouterLink>
        </div>

        <div v-if="formWriterName" class="flex flex-col sm:flex-row">
          <span class="text-gray-70 font-bold sm:w-32" v-text="nameLabelText" />
          <span class="text-gray-90" v-text="formWriterName" />
        </div>

        <div class="flex flex-col sm:flex-row">
          <span class="text-gray-70 font-bold sm:w-32" v-text="statusLabelText" />
          <span
            v-if="!isLoadingFormData"
            class="text-gray-90"
            v-text="deliveryStatus"
          />
        </div>
      </div>
    </div>
    <div class="bg-gray-5 max-w-[70ch] rounded-lg" :style>
      <FormWriter
        v-if="isFormWriterReady"
        :observer-mode="true"
        :item="formWriterData!"
        :pre-selected-student="relatedGroupMember"
      />
      <NotFound v-else-if="isError && hasRequiredContext" class="p-4" />
      <FormWriterSkeleton
        v-else-if="isAssignmentPage"
        class="p-4"
      />
      <div
        v-else
        class="text-gray-70 p-4 text-center"
        v-text="noAssignmentDataText"
      />
    </div>
  </div>
</template>
